// Loan dashboard — "View all" surface for the needs-attention feed and the
// designated rehome of the retired Due Dates tab's E1 bulk operations.
// Complete list + discipline / severity / owner filters; calendar-backed
// rows support per-item complete/snooze (via the existing item drawer) and
// E1/ADMIN bulk complete / bulk snooze (the same role gate the tab's
// toolbar applied).

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import {
  useCompleteCalendarItem,
  useLoanDueDates,
  useSnoozeCalendarItem,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { LoanAttentionItemDto } from "@/api/portfolioAdmin/loanHealthApi";
import type { ComplianceCalendarItemDto } from "@/api/portfolioAdmin/types";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { DueDateItemDrawer } from "../dueDates/DueDateItemDrawer";
import { daysLabel, daysUntil } from "../common/colorBand";
import { disciplineLabel, SEVERITY_DOT_CLASS } from "./healthDisplay";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface AttentionFeedDrawerProps {
  loanId: string;
  items: LoanAttentionItemDto[];
  open: boolean;
  onClose: () => void;
}

export function AttentionFeedDrawer({
  loanId,
  items,
  open,
  onClose,
}: AttentionFeedDrawerProps) {
  const navigate = useNavigate();
  const auth = useAuth();
  const canBulk = auth.hasRoleCode("E1") || auth.hasRoleCode("ADMIN");
  const dueDates = useLoanDueDates(loanId);

  const disciplines = useMemo(
    () => [...new Set(items.map((i) => i.discipline))],
    [items],
  );
  const owners = useMemo(
    () => [...new Set(items.map((i) => i.ownerRole))],
    [items],
  );

  const [disciplineFilter, setDisciplineFilter] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<"error" | "warning" | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);

  const filtered = items.filter(
    (i) =>
      (disciplineFilter === null || i.discipline === disciplineFilter) &&
      (severityFilter === null || i.severity === severityFilter) &&
      (ownerFilter === null || i.ownerRole === ownerFilter),
  );

  // Bulk selection — calendar-backed rows only (complete/snooze are
  // calendar operations; derived rows resolve in their own module).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (calendarItemId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(calendarItemId)) next.delete(calendarItemId);
      else next.add(calendarItemId);
      return next;
    });

  const [openItem, setOpenItem] = useState<ComplianceCalendarItemDto | null>(null);
  const [bulkSnoozeOpen, setBulkSnoozeOpen] = useState(false);

  const toast = useToast();
  const complete = useCompleteCalendarItem(loanId);

  const onBulkComplete = async () => {
    let ok = 0;
    let fail = 0;
    for (const id of selected) {
      try {
        await complete.mutateAsync({ itemId: id, body: { notes: null } });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    if (fail === 0) toast.success(`Marked ${ok} items complete.`);
    else toast.error(`Completed ${ok}; ${fail} failed.`);
    setSelected(new Set());
  };

  const onRowClick = (row: LoanAttentionItemDto) => {
    if (row.calendarItemId) {
      const dto = (dueDates.data ?? []).find((i) => i.id === row.calendarItemId);
      if (dto) {
        setOpenItem(dto);
        return;
      }
    }
    onClose();
    navigate(`/portfolio-admin/loans/${loanId}?tab=${row.targetTab}`);
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title="Needs attention"
      subtitle={`${items.length} open item${items.length === 1 ? "" : "s"} across all disciplines`}
      width="lg"
      testId="attention-feed-drawer"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="attention-feed-body">
        <div className="flex flex-wrap gap-space-2">
          <FilterChips
            label="Discipline"
            options={disciplines.map((d) => ({ value: d, label: disciplineLabel(d) }))}
            value={disciplineFilter}
            onChange={setDisciplineFilter}
            testId="attention-filter-discipline"
          />
          <FilterChips
            label="Severity"
            options={[
              { value: "error", label: "Error" },
              { value: "warning", label: "Warning" },
            ]}
            value={severityFilter}
            onChange={(v) => setSeverityFilter(v as "error" | "warning" | null)}
            testId="attention-filter-severity"
          />
          <FilterChips
            label="Owner"
            options={owners.map((o) => ({ value: o, label: o }))}
            value={ownerFilter}
            onChange={setOwnerFilter}
            testId="attention-filter-owner"
          />
        </div>

        {canBulk && selected.size > 0 && (
          <div
            className="flex items-center gap-space-3 rounded-md bg-onyx text-white-pure px-space-3 py-space-2"
            data-testid="attention-bulk-toolbar"
          >
            <span className="text-small">{selected.size} selected</span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void onBulkComplete()}
              disabled={complete.isPending}
              data-testid="attention-bulk-complete"
            >
              Bulk mark complete
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setBulkSnoozeOpen(true)}
              data-testid="attention-bulk-snooze"
            >
              Bulk snooze
            </Button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-caption underline ml-auto"
            >
              Clear selection
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="text-small text-gray-mid m-0" data-testid="attention-feed-empty">
            No items match the filters.
          </p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col" data-testid="attention-feed-list">
            {filtered.map((row) => (
              <li
                key={row.id}
                className="flex items-center gap-space-2 py-space-1 border-b border-border-light last:border-b-0"
              >
                {canBulk && row.calendarItemId ? (
                  <input
                    type="checkbox"
                    checked={selected.has(row.calendarItemId)}
                    onChange={() => toggle(row.calendarItemId!)}
                    aria-label={`Select ${row.title}`}
                    data-testid={`attention-select-${row.id}`}
                  />
                ) : (
                  canBulk && <span className="w-[13px] shrink-0" aria-hidden="true" />
                )}
                <button
                  type="button"
                  onClick={() => onRowClick(row)}
                  className="flex items-center gap-space-2 flex-1 min-w-0 py-space-1 px-space-1 rounded-sm text-left hover:bg-white-warm"
                  data-testid={`attention-feed-row-${row.id}`}
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block h-2 w-2 rounded-full shrink-0 ${SEVERITY_DOT_CLASS[row.severity]}`}
                  />
                  <span className="text-caption text-gray-mid uppercase tracking-label shrink-0 w-20">
                    {disciplineLabel(row.discipline)}
                  </span>
                  <span className="text-small text-onyx truncate flex-1" title={row.title}>
                    {row.title}
                  </span>
                  {row.dueDate && (
                    <span className="text-caption text-gray-mid tabular-nums shrink-0">
                      {daysLabel(daysUntil(row.dueDate))}
                    </span>
                  )}
                  <span className="text-caption text-gray-mid border border-border-light rounded-full px-space-1 shrink-0">
                    {row.ownerRole}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {openItem && (
        <DueDateItemDrawer
          loanId={loanId}
          item={openItem}
          open={!!openItem}
          onClose={() => setOpenItem(null)}
        />
      )}

      {bulkSnoozeOpen && (
        <BulkSnoozeModal
          loanId={loanId}
          itemIds={Array.from(selected)}
          open={bulkSnoozeOpen}
          onClose={() => setBulkSnoozeOpen(false)}
          onDone={() => setSelected(new Set())}
        />
      )}
    </ResourceDrawer>
  );
}

function FilterChips({
  label,
  options,
  value,
  onChange,
  testId,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (v: string | null) => void;
  testId: string;
}) {
  if (options.length === 0) return null;
  return (
    <div className="flex items-center gap-space-1 flex-wrap" data-testid={testId}>
      <span className="text-caption text-gray-mid uppercase tracking-label">{label}</span>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(active ? null : o.value)}
            className={`text-caption rounded-full border px-space-2 py-[2px] ${
              active
                ? "border-onyx bg-onyx text-white-pure"
                : "border-border-light bg-white-pure text-gray-mid hover:bg-white-warm"
            }`}
            aria-pressed={active}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Bulk snooze — one date + reason applied to every selected calendar
 *  item (per-item failures are non-fatal, mirroring bulk complete). */
function BulkSnoozeModal({
  loanId,
  itemIds,
  open,
  onClose,
  onDone,
}: {
  loanId: string;
  itemIds: string[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const snooze = useSnoozeCalendarItem(loanId);
  const tz = useUserTimeZone();
  const defaultDate = isoDateInTz(new Date(Date.now() + 7 * 86_400_000), tz);
  const [newDueDate, setNewDueDate] = useState(defaultDate);
  const [reason, setReason] = useState("");

  const onSubmit = async () => {
    if (!reason.trim()) {
      toast.error("A reason is required to snooze.");
      return;
    }
    let ok = 0;
    let fail = 0;
    for (const id of itemIds) {
      try {
        await snooze.mutateAsync({ itemId: id, body: { newDueDate, reason } });
        ok += 1;
      } catch (err) {
        fail += 1;
        if (err instanceof ApiError) {
          // continue; per-item failures are non-fatal in bulk.
        }
      }
    }
    if (fail === 0) toast.success(`Snoozed ${ok} items.`);
    else toast.error(`Snoozed ${ok}; ${fail} failed.`);
    onDone();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={`Snooze ${itemIds.length} items`} size="sm">
      <div className="flex flex-col gap-space-3" data-testid="bulk-snooze-modal">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">New due date</span>
          <input
            type="date"
            value={newDueDate}
            onChange={(e) => setNewDueDate(e.target.value)}
            className="h-9 border border-border-light rounded-md px-space-2 text-small"
            data-testid="bulk-snooze-date"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Reason</span>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            data-testid="bulk-snooze-reason"
          />
        </label>
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={snooze.isPending}
            data-testid="bulk-snooze-submit"
          >
            Snooze
          </Button>
        </div>
      </div>
    </Modal>
  );
}
